import {blue, cyan, deepPurple, grey, purple} from '@mui/material/colors'
import type {Theme} from '@mui/material/styles'
import type {GridRowSelectionModel} from '@mui/x-data-grid'
import type {DatumValue} from '@nivo/core'
import {extent, max, min, scaleBand, scaleLinear, scaleTime, select} from 'd3'
import type {ScaleBand, ScaleLinear, ScaleTime} from 'd3'
import {format, formatDistance, isSameDay, isSameWeek, subDays} from 'date-fns'
import {de, enUS, es, fr} from 'date-fns/locale'
import type {BaseSyntheticEvent, Dispatch, MutableRefObject, SetStateAction} from 'react'
import type {TFunction} from 'react-i18next'
import {timeParser} from './shared/constants/d3Graph'
import type {DensityInterface} from './shared/hooks/useProductionTimelineDensityOptions.ts'
import type {AnomalyWithReasonInterface} from './shared/interfaces/anomaly.ts'
import type {CockpitWarningInterface} from './shared/interfaces/cockpitWarning.ts'
import type {DaysHoursMinInterface} from './shared/interfaces/daysHoursMin'
import type {ProgramClassificationInterface} from './shared/interfaces/programClassification'
import {getClassificationName} from './shared/interfaces/program'
import type {ProgramInterface} from './shared/interfaces/program'
import type {
  SliceTooltipTracesInterface,
  TraceDataInterface,
  TraceInterface,
} from './shared/interfaces/trace'
import type {UserInterface} from './shared/interfaces/user'
import type {LegendItemInterface} from '@shared/interfaces/nivoGraph'
import type {OverlayedSignalResponseInterface} from '@shared/interfaces/signal'

type LanguageCode = 'de' | 'en' | 'es' | 'fr'

const getTokenFromURL = (): string | null => {
  try {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const token = urlParams.get('token')
    return token || null
  } catch (_error) {
    return null
  }
}

export const getToken = (): string | null => {
  const localToken = localStorage.getItem('token')
  if (localToken) {
    return localToken
  }
  return getTokenFromURL()
}

export const getDefaultPreferredLanguage = (): string => {
  const preferredLanguage = localStorage.getItem('mbai-preferred-language')
  return preferredLanguage || 'en'
}

export const getRolesObject = (roles: string[]): {[key: string]: boolean} => {
  const roleObject: {[key: string]: boolean} = {}
  roles.forEach((item: string) => {
    roleObject[item] = true
  })
  return roleObject
}

export const getUserFullName = (
  user: {first_name: string; last_name: string} | UserInterface,
): string | null => {
  try {
    const firstName = user.first_name ? user.first_name : ''
    const lastName = user.last_name ? user.last_name : ''
    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    }
    if (firstName) {
      return firstName
    }
    if (lastName) {
      return lastName
    }
    return ''
  } catch (_e) {
    return null
  }
}

const getSubstring = (str: string, startMarker: string, endMarker: string): string => {
  const start = str.indexOf(startMarker)
  if (start === -1) {
    return '0'
  }
  const end = str.indexOf(endMarker, start)
  if (end === -1) {
    return '0'
  }
  return str.substring(start + 1, end)
}

// Returns units of time based on an ISOString duration
export const ISOStringToUnits = (ISOString: string): DaysHoursMinInterface => {
  let days = getSubstring(ISOString, 'P', 'D')
  if (days.includes('M')) {
    ;[, days] = days.split('M')
  } else if (days.includes('Y')) {
    ;[, days] = days.split('Y')
  }
  let hours = getSubstring(ISOString, 'T', 'H')
  let minutes = getSubstring(ISOString, 'H', 'M')

  // Handle seconds correctly
  const secondsMatch = ISOString.match(/(\d+(\.\d+)?)S/)
  let seconds = '0'
  if (secondsMatch) {
    const secondsString = secondsMatch[1]
    const secondsNumber = parseFloat(secondsString)
    seconds = Math.round(secondsNumber).toString()
  }

  // If any of the values is 0, return an empty string
  seconds = seconds === '0' ? '' : seconds
  minutes = minutes === '0' ? '' : minutes
  hours = hours === '0' ? '' : hours
  days = days === '0' ? '' : days

  return {
    days,
    hours,
    minutes,
    seconds,
  }
}

export const getUrlParams = (
  arr: string[] | GridRowSelectionModel | ProgramInterface[],
  param: string,
): string => {
  const newArray: string[] = []
  arr.forEach((item, key) => {
    newArray[key] = `${param}=${item}`
  })
  return newArray.join('&')
}

export const sleep = (ms: number): Promise<typeof setTimeout> =>
  new Promise((resolve) => setTimeout(resolve, ms))

export const getUniqueValuesFromArray = <T, Key extends keyof T>(
  array: T[],
  key?: Key,
): T[] | T[Key][] => {
  let newArray
  let finalArray: T[] | T[Key][]

  // 1: Get simple arrays
  if (key) {
    // If there is a key, transform
    newArray = Array.from(new Set(array.map((item) => item[key])))
  } else {
    newArray = Array.from(new Set(array))
  }

  // 2: Built the objects back using the simple arrays
  if (key) {
    finalArray = newArray.map((item) => array.find((i: T) => i[key] === item)) as Array<T>
  } else {
    finalArray = newArray
  }
  return finalArray
}

// Searches for searchTerm, in searchList (array of objects) using searchProperties
export const searchItems = <T>(
  searchTerm: string,
  searchList: T[],
  searchProperties: string[],
): T[] => {
  let newList
  if (!searchTerm) {
    newList = searchList
  } else {
    newList = searchList.filter((item) =>
      searchProperties.some((s: string) => {
        if (item[s as keyof typeof item]) {
          return (item[s as keyof typeof item] as string)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        }
        return null
      }),
    )
  }
  return newList
}

// Compares two objects with similar models to determine if they are equal.
export const areObjectsEqual = <T>(firstObject: T, secondObject: T): boolean => {
  for (const property in firstObject) {
    if (typeof firstObject[property] === 'string') {
      if (secondObject && firstObject[property] !== secondObject[property]) {
        return false
      }
    } else if (typeof firstObject[property] === 'object') {
      if (
        secondObject &&
        typeof firstObject[property] === 'object' &&
        firstObject[property] !== null &&
        !areObjectsEqual(firstObject[property] as object, secondObject[property] as object)
      ) {
        return false
      }
    }
  }
  return true
}

export const getLongestStringFromArray = (array: string[]): string | null => {
  if (array.length === 0) {
    return null
  }
  return array.reduce((a, b) => (a.length > b.length ? a : b))
}

export const areObjectKeysEqual = (firstObject: object, secondObject: object): boolean => {
  const keys1 = Object.keys(firstObject)
  const keys2 = Object.keys(secondObject)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    if (!keys2.includes(key)) {
      return false
    }

    const value1 = firstObject[key as keyof typeof firstObject]
    const value2 = secondObject[key as keyof typeof secondObject]

    if (typeof value1 === 'object' && typeof value2 === 'object') {
      if (!areObjectKeysEqual(value1, value2)) {
        return false
      }
    }
  }

  return true
}

export const handleValidation = (val: string): boolean => {
  const regex =
    /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/i
  return regex.test(val)
}

export const handleBasicInputChange = (
  event: BaseSyntheticEvent,
  setNewValue: Dispatch<SetStateAction<FormDataEntryValue>>,
): void => {
  const {value} = event.target
  setNewValue(value)
}

// format "00:01:18.240966"
export const hourToString = (hour: string): string => {
  const hourArray = hour.split(':')
  let hourString
  if (hourArray[0] === '00') {
    hourArray.shift()
    hourString = `${hourArray[0]}m ${Math.round(Number(hourArray[1])).toString()}s`
  } else {
    hourString = `${hourArray[0]}h ${hourArray[1]}m ${Math.round(Number(hourArray[2])).toString()}s`
  }
  return hourString
}

interface OptionsProps {
  showDate?: boolean
  showYear?: boolean
  showTime?: boolean
  showSeconds?: boolean
  showDayOfTheWeek?: boolean
}

const LANGUAGE_LOCALE_MAP: Record<LanguageCode, Locale> = {
  de,
  en: enUS,
  es,
  fr,
}

/**
 * `date` shifted so that formatting it with the browser's local accessors
 * renders `timeZone`'s wall clock.
 *
 * date-fns v3 has no timezone support and `format()` always reads the browser's
 * zone, so a user whose profile zone differs from their machine saw every
 * timestamp in machine time — anomaly instances, threshold violations, entries
 * — while the production timeline binned in the profile zone. Rebasing the
 * instant here fixes all of them at once, without touching call sites.
 *
 * The result is a "fake" Date: correct to read field-by-field, meaningless to
 * compare against a real instant: format it, never compare or send it.
 */
export const rebaseToZone = (date: Date, timeZone: string): Date => {
  if (!timeZone) {
    return date
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(date)
    const field = (type: string): number => {
      const found = parts.find((part) => part.type === type)
      return found ? Number(found.value) : 0
    }
    const rebased = new Date(date.getTime())
    // setFullYear/setHours together avoid the intermediate-overflow trap of
    // setting month before day (e.g. 31 Jan -> setMonth(1) -> 3 Mar).
    rebased.setFullYear(field('year'), field('month') - 1, field('day'))
    rebased.setHours(field('hour') % 24, field('minute'), field('second'), 0)
    return rebased
  } catch {
    // Unrecognized IANA name: Intl throws. Fall back to the browser's zone
    // rather than dropping the timestamp.
    return date
  }
}

// The inverse of `rebaseToZone`: `wallTime`'s browser-local fields read as a
// wall-clock time in `timeZone`, back to the instant it names. Two corrections
// converge across a DST change; a time skipped by a spring-forward gap or
// repeated by an autumn fall-back resolves to one of its nearby instants.
export const fromZoneWallTime = (wallTime: Date, timeZone: string): Date => {
  if (!timeZone) {
    return wallTime
  }
  let instant = wallTime.getTime()
  for (let step = 0; step < 2; step += 1) {
    instant += wallTime.getTime() - rebaseToZone(new Date(instant), timeZone).getTime()
  }
  return new Date(instant)
}

/**
 * The zone the app reads a user's dates in: the profile's `preferred_timezone`,
 * or '' when the profile names none, which `rebaseToZone` reads as the
 * browser's own zone (AB#5973).
 */
export const profileTimeZone = (user: UserInterface | null | undefined): string =>
  user?.userprofile?.preferred_timezone || ''

export const getFormattedDate = (
  date: Date | string | DatumValue,
  user: UserInterface | null,
  {
    showDate = true,
    showYear = false,
    showTime = false,
    showSeconds = false,
    showDayOfTheWeek = false,
  }: OptionsProps,
): string => {
  if (!date) {
    return ''
  }

  let newDate: Date
  let dayMonth: string
  let hourMinute: string
  let amPm: string

  if (user && user.userprofile) {
    switch (user.userprofile.preferred_locale) {
      case 'en':
        dayMonth = 'MM/dd'
        hourMinute = 'hh:mm'
        amPm = ' aaa'
        break
      case 'en-gb':
        dayMonth = 'dd/MM'
        hourMinute = 'HH:mm'
        amPm = ''
        break
      case 'de':
        dayMonth = 'dd.MM'
        hourMinute = 'HH:mm'
        amPm = ''
        break
      default:
        dayMonth = 'MM/dd'
        hourMinute = 'hh:mm'
        amPm = ''
        break
    }
  } else {
    dayMonth = 'dd/MM'
    hourMinute = 'HH:mm'
    amPm = ''
  }

  const year =
    showYear && showDate && user?.userprofile?.preferred_locale === 'de'
      ? '.y'
      : showYear && showDate
        ? '/y'
        : ''
  const time = showDate && showTime ? ` | ${hourMinute}` : showTime ? hourMinute : ''
  const seconds = showSeconds ? ':ss' : ''
  const dayMonthString = showDate ? dayMonth : ''
  const amPmString = showTime ? amPm : ''

  // Get the new date
  if (!Number.isNaN(new Date(date).getDate())) {
    newDate = new Date(date)
  } else {
    newDate = date as Date
  }

  // Render in the user's PROFILE zone — the same zone pm-core bins the timeline
  // in (it reads it off the auth session). Empty profile keeps the browser's
  // zone, which is the previous behaviour (AB#5973).
  newDate = rebaseToZone(newDate, profileTimeZone(user))

  const formatString = dayMonthString + year + time + seconds + amPmString

  const rawLocale = user?.userprofile?.preferred_language || 'en'
  const localeKey = rawLocale.toLowerCase().split('-')[0] as LanguageCode
  const locale = LANGUAGE_LOCALE_MAP[localeKey] || enUS

  let formattedDate = format(newDate, formatString, {locale})

  if (showDayOfTheWeek) {
    const dayOfWeek = format(newDate, 'EEE', {locale})
    formattedDate += ` (${dayOfWeek})`
  }

  return formattedDate
}

export const getFormattedDateWithContext = (
  date: Date | string | DatumValue,
  user: UserInterface | null,
  options: OptionsProps,
): string => {
  if (!date) {
    return ''
  }

  let parsedDate: Date

  // Parse the date safely
  if (!Number.isNaN(new Date(date).getDate())) {
    parsedDate = new Date(date)
  } else {
    parsedDate = date as Date
  }

  // "Today"/"Yesterday"/the weekday name are calendar judgements, so they have
  // to be made in the user's profile zone — and against *now* in that same zone,
  // or a timestamp near midnight is labelled off by a day. date-fns' isToday /
  // isYesterday / isThisWeek all read the browser's zone, so they are replaced
  // by the same comparison against a rebased now (AB#5973). `parsedDate` itself
  // is still handed to getFormattedDate, which rebases internally.
  const timeZone = profileTimeZone(user)
  const zonedDate = rebaseToZone(parsedDate, timeZone)
  const zonedNow = rebaseToZone(new Date(), timeZone)

  // Handle "Today" case
  if (isSameDay(zonedDate, zonedNow)) {
    return getFormattedDate(parsedDate, user, {
      showDate: false,
      showTime: true,
      ...options,
    })
  }

  // Handle "Yesterday" case
  if (isSameDay(zonedDate, subDays(zonedNow, 1))) {
    return `Yesterday ${getFormattedDate(parsedDate, user, {
      showDate: false,
      showTime: true,
      ...options,
    })}`
  }

  // Handle "This week" case
  if (isSameWeek(zonedDate, zonedNow)) {
    return `${format(zonedDate, 'EEEE')} ${getFormattedDate(parsedDate, user, {
      showDate: false,
      showTime: true,
      ...options,
    })}`
  }

  // Fallback to existing getFormattedDate for other cases
  return getFormattedDate(parsedDate, user, options)
}

export const lastSeenFunction = (lastSeen: Date, user: UserInterface | null): string => {
  const lastSeenDate = new Date(lastSeen)
  const newDate = new Date()
  const difference = newDate.getTime() - lastSeenDate.getTime()
  const daysDifference = difference / (1000 * 60 * 60 * 24)
  const preferred_language = user?.userprofile?.preferred_language || 'en'
  const locale = LANGUAGE_LOCALE_MAP[preferred_language as LanguageCode] || enUS
  if (daysDifference < 1) {
    return formatDistance(new Date(lastSeenDate), new Date(), {addSuffix: true, locale})
  }
  return getFormattedDate(lastSeenDate, user, {
    showYear: true,
    showTime: true,
    showSeconds: true,
  })
}

export const formatHeaderXValue = (value: number | Date, user: UserInterface): string => {
  let xValue: string
  try {
    if (typeof value === 'number') {
      xValue = `Minute: ${Math.round((value + Number.EPSILON) * 100) / 100}`
    } else {
      xValue = getFormattedDate(value, user, {
        showYear: true,
        showTime: true,
        showSeconds: true,
      })
    }
    return xValue
  } catch (error) {
    console.error(error)
    return ''
  }
}

export const findClosestCircle = (
  xPosition: number,
  d3SVGRef: MutableRefObject<HTMLDivElement | null>,
): SVGCircleElement | null => {
  const {current} = d3SVGRef
  if (!current) {
    return null
  }
  const svg = select(current)
  const allCircles = svg.selectAll('.main-graph-data-point')
  let closestCircle = null
  let minDifference = Number.MAX_SAFE_INTEGER

  allCircles.each((_, i, nodes) => {
    const currentCx = parseFloat(select(nodes[i]).attr('cx') || '0')
    const difference = Math.abs(currentCx - xPosition)

    if (difference < minDifference) {
      minDifference = difference
      closestCircle = nodes[i]
    }
  })
  return closestCircle
}

/**
 * Performs binary search to find the closest point in a sorted array to a given x position
 * @param data Array of data points sorted by x value
 * @param xPosition Target x position to find closest point to
 * @param xScale Function to convert data x values to screen coordinates
 * @returns Index of the closest point in the data array
 */
const binarySearchClosestPoint = (
  data: TraceDataInterface[],
  xPosition: number,
  xScale: (x: number) => number,
): number => {
  let left = 0
  let right = data.length - 1
  let closestIndex = -1
  let closestDistance = Infinity

  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    const dataX = parseFloat(data[mid].x)

    if (Number.isNaN(dataX)) {
      right = mid - 1
      continue
    }

    const scaledDataX = xScale(dataX)
    const currentDistance = Math.abs(scaledDataX - xPosition)

    if (currentDistance < closestDistance) {
      closestDistance = currentDistance
      closestIndex = mid
    }

    if (scaledDataX < xPosition) {
      left = mid + 1
    } else {
      right = mid - 1
    }
  }

  return closestIndex
}

/**
 * Refines the search for the closest point by checking nearby points
 * @param data Array of data points
 * @param initialIndex Initial closest point index found by binary search
 * @param xPosition Target x position to find closest point to
 * @param xScale Function to convert data x values to screen coordinates
 * @returns Index of the closest point after refinement
 */
const refineClosestPointSearch = (
  data: TraceDataInterface[],
  initialIndex: number,
  xPosition: number,
  xScale: (x: number) => number,
): number => {
  if (initialIndex === -1) {
    return -1
  }

  let closestIndex = initialIndex
  let closestDistance = Infinity

  const initialX = parseFloat(data[initialIndex].x)
  if (!Number.isNaN(initialX)) {
    closestDistance = Math.abs(xScale(initialX) - xPosition)
  }

  const startIdx = Math.max(0, initialIndex - 3)
  const endIdx = Math.min(data.length, initialIndex + 3)

  for (let i = startIdx; i < endIdx; i++) {
    if (i === initialIndex) {
      continue
    }

    const dataX = parseFloat(data[i].x)
    if (Number.isNaN(dataX)) {
      continue
    }

    const scaledDataX = xScale(dataX)
    const currentDistance = Math.abs(scaledDataX - xPosition)

    if (currentDistance < closestDistance) {
      closestDistance = currentDistance
      closestIndex = i
    }
  }

  return closestIndex
}

/**
 * Finds the closest data point to a given x position
 * @param data Array of data points
 * @param xPosition Target x position to find closest point to
 * @param xScale Function to convert data x values to screen coordinates
 * @returns The closest data point or null if no data
 */
const findClosestPoint = (
  data: TraceDataInterface[],
  xPosition: number,
  xScale: (x: number) => number,
  searchRadius: number = 50, // Default search radius in pixels
): TraceDataInterface | null => {
  if (!data || data.length === 0) {
    return null
  }

  const closestIndex = binarySearchClosestPoint(data, xPosition, xScale)
  const refinedIndex = refineClosestPointSearch(data, closestIndex, xPosition, xScale)

  if (refinedIndex === -1) {
    return null
  }

  const point = data[refinedIndex]
  const dataX = parseFloat(point.x)
  if (Number.isNaN(dataX)) {
    return null
  }

  const scaledDataX = xScale(dataX)
  const distance = Math.abs(scaledDataX - xPosition)

  // If the closest point is within the search radius, return it
  if (distance <= searchRadius) {
    return point
  }

  // If we're at the maximum search radius, return the closest point anyway
  // This ensures we always return something if there's data available
  if (searchRadius >= 1000) {
    return point
  }

  // Otherwise, recursively expand the search radius and try again
  const expandedResult = findClosestPoint(data, xPosition, xScale, searchRadius * 2)

  // If we couldn't find a point with the expanded radius, return the current point
  // This ensures we always return something rather than null
  return expandedResult || point
}

export const getTracesFromXPosition = (
  traceData: (TraceInterface | OverlayedSignalResponseInterface)[],
  d3SVGRef: MutableRefObject<HTMLDivElement | null>,
  xPosition: number,
  xScale: ScaleLinear<number, number> | ScaleTime<number, number>,
  batchesStates: LegendItemInterface[],
  useClosestPointPerTrace: boolean = false,
): SliceTooltipTracesInterface[] => {
  // Calculate max distance threshold for closest-point-per-trace mode
  // Use 5% of the domain width as reasonable bounds
  const [domainMin, domainMax] = xScale.domain().map(Number)
  const domainWidthPixels = Math.abs(xScale(domainMax) - xScale(domainMin))
  const maxDistancePixels = domainWidthPixels * 0.05 // 5% of domain width

  return traceData
    .flatMap((trace) => {
      const mappedData = trace.data.map((data) => {
        const parsedDataX = timeParser(data.x)
        const dataX = parsedDataX ? xScale(parsedDataX) : xScale(parseFloat(data.x))
        return {
          trace,
          data,
          dataX,
        }
      })

      if (useClosestPointPerTrace) {
        // Find the closest point for this trace regardless of exact position
        // but within reasonable bounds (5% of domain width)
        const sorted = mappedData.sort(
          (a, b) => Math.abs(a.dataX - xPosition) - Math.abs(b.dataX - xPosition),
        )
        const closest = sorted[0]
        if (closest && Math.abs(closest.dataX - xPosition) <= maxDistancePixels) {
          return [closest]
        }

        // If no point within threshold, find the "last known value"
        // This is the closest point that's BEFORE (to the left of) the cursor position
        // This ensures we always show data in gaps instead of "No data available"
        const lastKnownValue = mappedData
          .filter((point) => point.dataX <= xPosition)
          .sort((a, b) => b.dataX - a.dataX)[0]

        if (lastKnownValue) {
          return [lastKnownValue]
        }

        return []
      }

      // Non-overlay mode: strict matching - only include points within 0.01 pixels
      return mappedData
        .filter(({dataX}) => Math.abs(dataX - xPosition) < 0.01)
        .sort((a, b) => Math.abs(a.dataX - xPosition) - Math.abs(b.dataX - xPosition))
        .slice(0, 1)
    })
    .map((selectedPoint) => {
      if (!selectedPoint) {
        return null
      }

      const {current} = d3SVGRef
      if (!current) {
        return null
      }

      const svg = select(current)
      const circles = svg.selectAll('.main-graph-data-point')

      const currentColorElement = circles
        .filter(function () {
          const traceId = (selectedPoint.trace as TraceInterface).uuid || selectedPoint.trace.id
          return select(this).attr('data-name') === traceId
        })
        .node()

      const currentColor = currentColorElement
        ? select(currentColorElement).attr('fill') || 'black'
        : 'black'

      // Find the corresponding batch state
      const matchingBatch = batchesStates.find(
        (batch) =>
          batch.batchId === selectedPoint.trace.id ||
          batch.batchId === (selectedPoint.trace as TraceInterface).uuid,
      )

      // Ensure the batch is selected before including it
      if (matchingBatch && matchingBatch.selected) {
        return {
          trace: selectedPoint.trace,
          value: selectedPoint.data.original_y
            ? selectedPoint.data.original_y
            : selectedPoint.data.y,
          color: currentColor,
          closestPoint: selectedPoint.data,
        }
      }

      return null
    })
    .filter(Boolean) as SliceTooltipTracesInterface[]
}

export const getTracesFromXPositionRelativeMode = (
  traceData: (TraceInterface | OverlayedSignalResponseInterface)[],
  d3SVGRef: MutableRefObject<HTMLDivElement | null>,
  xPosition: number,
  xScale: ScaleLinear<number, number> | ScaleTime<number, number>,
  batchesStates: LegendItemInterface[],
  customSearchRadius?: number,
): SliceTooltipTracesInterface[] => {
  if (!traceData.length) {
    return []
  }

  // Get the x-axis domain range
  const [xMin, xMax] = xScale.domain().map(Number)

  // Use a more generous threshold for model instance graphs (10% of domain width)
  // This ensures we capture points that are close but not exactly at the same x position
  // If a custom search radius is provided, use that instead
  const xThresholdPixels = customSearchRadius
    ? Math.abs(xScale(xMin + customSearchRadius) - xScale(xMin))
    : Math.abs(xScale(xMin + (xMax - xMin) * 0.1) - xScale(xMin))

  // Performance optimization: Pre-filter traces that might be in range
  // This avoids processing traces that are definitely out of range
  const potentialTraces = traceData.filter((trace) => {
    if (!trace.data || trace.data.length === 0) {
      return false
    }

    // Always include model series traces as they're important for tooltips
    const traceId = (trace as TraceInterface).uuid ?? trace.id
    const isModelSeries = ['upper_limit', 'lower_limit', 'median', 'mean'].some((term) =>
      traceId.toLowerCase().includes(term),
    )

    if (isModelSeries) {
      return true
    }

    // For other traces, check if they might be in range
    return true // Include all traces and let findClosestPoint handle the search radius
  })

  // If no traces might be in range, return empty array
  if (potentialTraces.length === 0) {
    return []
  }

  // Map to store closest point per trace
  const closestPointsMap = new Map<
    string,
    {
      trace: TraceInterface | OverlayedSignalResponseInterface
      data: TraceDataInterface
      dataX: number
      distance: number // Store distance for sorting
      batchNumber: number // Store batch number for sorting
    }
  >()

  let globalClosestX: number | null = null
  let minGlobalDistance = Number.MAX_SAFE_INTEGER

  // First pass: Find the closest point across all traces
  for (const trace of potentialTraces) {
    // Skip empty traces
    if (!trace.data || trace.data.length === 0) {
      continue
    }

    // Use the expanded search radius in findClosestPoint
    const closestData = findClosestPoint(trace.data, xPosition, xScale)
    if (!closestData) {
      continue
    }

    const dataX = parseFloat(closestData.x)
    const scaledDataX = xScale(dataX)
    const currentDistance = Math.abs(scaledDataX - xPosition)

    if (currentDistance < minGlobalDistance) {
      minGlobalDistance = currentDistance
      globalClosestX = scaledDataX
    }
  }

  // If we didn't find any global closest point, use the first point of the first trace with data
  if (globalClosestX === null) {
    for (const trace of potentialTraces) {
      if (trace.data && trace.data.length > 0) {
        const firstPoint = trace.data[0]
        const dataX = parseFloat(firstPoint.x)
        if (!Number.isNaN(dataX)) {
          globalClosestX = xScale(dataX)
          break
        }
      }
    }

    // If we still don't have a point, return empty array
    if (globalClosestX === null) {
      return []
    }
  }

  // Second pass: Find points from all traces close to the global closest X
  // Use a more generous threshold based on the distance to the closest point
  // This ensures we include relevant points even when they're far from the cursor
  const dynamicThreshold = Math.max(xThresholdPixels, minGlobalDistance * 1.5)

  for (const trace of potentialTraces) {
    const traceId = (trace as TraceInterface).uuid ?? trace.id

    if (!traceId || !trace.data || trace.data.length === 0) {
      continue
    }

    let batchNumber = Infinity

    const isModelSeries = ['upper_limit', 'lower_limit', 'median', 'mean'].some((term) =>
      traceId.toLowerCase().includes(term),
    )

    if (!isModelSeries) {
      if ('name' in trace && typeof trace.name === 'string') {
        const nameMatch = String(trace.name).match(/batch\s*(\d+)/i)
        if (nameMatch && nameMatch[1]) {
          batchNumber = parseInt(nameMatch[1], 10)
        }
      }

      if (batchNumber === Infinity) {
        const idMatch = traceId.match(/batch\s*(\d+)/i) || traceId.match(/(\d+)$/)
        if (idMatch && idMatch[1]) {
          batchNumber = parseInt(idMatch[1], 10)
        } else {
          batchNumber = 9999
        }
      }
    }

    // Use different thresholds for model series vs batch data
    const seriesThreshold = isModelSeries ? dynamicThreshold * 0.5 : dynamicThreshold

    // Use the expanded search radius in findClosestPoint
    const closestData = findClosestPoint(trace.data, globalClosestX, xScale)

    if (!closestData) {
      // If we can't find a close point but the trace has data, use its first point
      if (trace.data.length > 0) {
        const firstPoint = trace.data[0]
        const dataX = parseFloat(firstPoint.x)
        if (!Number.isNaN(dataX)) {
          const scaledDataX = xScale(dataX)
          closestPointsMap.set(traceId, {
            trace,
            data: firstPoint,
            dataX: scaledDataX,
            distance: Math.abs(scaledDataX - globalClosestX),
            batchNumber,
          })
        }
      }
      continue
    }

    const dataX = parseFloat(closestData.x)
    const scaledDataX = xScale(dataX)
    const distanceToGlobalX = Math.abs(scaledDataX - globalClosestX)

    // For model series, always include them regardless of distance
    // For batch data, use the dynamic threshold
    if (!isModelSeries && distanceToGlobalX > seriesThreshold) {
      continue
    }

    closestPointsMap.set(traceId, {
      trace,
      data: closestData,
      dataX: scaledDataX,
      distance: distanceToGlobalX,
      batchNumber,
    })
  }

  // If we still don't have any points in the map, use the first point of the first trace
  if (closestPointsMap.size === 0 && potentialTraces.length > 0) {
    const firstTrace = potentialTraces[0]
    if (firstTrace.data && firstTrace.data.length > 0) {
      const traceId = (firstTrace as TraceInterface).uuid ?? firstTrace.id
      const firstPoint = firstTrace.data[0]
      const dataX = parseFloat(firstPoint.x)
      if (!Number.isNaN(dataX)) {
        const scaledDataX = xScale(dataX)
        closestPointsMap.set(traceId, {
          trace: firstTrace,
          data: firstPoint,
          dataX: scaledDataX,
          distance: 0,
          batchNumber: 0,
        })
      }
    }
  }

  if (!d3SVGRef.current) {
    return []
  }

  const svg = select(d3SVGRef.current)
  const circles = svg.selectAll('.main-graph-data-point') // Cache selection **outside** loop

  return [...closestPointsMap.values()]
    .sort((a, b) => {
      const aId = (a.trace as TraceInterface).uuid ?? a.trace.id
      const bId = (b.trace as TraceInterface).uuid ?? b.trace.id

      // Sort by trace type first (model series before batches)
      const aIsModelSeries = ['upper_limit', 'lower_limit', 'median', 'mean'].some((term) =>
        aId.toLowerCase().includes(term),
      )
      const bIsModelSeries = ['upper_limit', 'lower_limit', 'median', 'mean'].some((term) =>
        bId.toLowerCase().includes(term),
      )

      if (aIsModelSeries && !bIsModelSeries) {
        return -1
      }
      if (!aIsModelSeries && bIsModelSeries) {
        return 1
      }

      if (!aIsModelSeries && !bIsModelSeries) {
        return a.batchNumber - b.batchNumber
      }

      if (aIsModelSeries && bIsModelSeries) {
        const modelOrder = {
          upper_limit: 0,
          lower_limit: 1,
          median: 2,
          mean: 3,
        }

        let aOrder = 999
        let bOrder = 999

        Object.entries(modelOrder).forEach(([term, order]) => {
          if (aId.toLowerCase().includes(term)) {
            aOrder = order
          }
          if (bId.toLowerCase().includes(term)) {
            bOrder = order
          }
        })

        return aOrder - bOrder
      }

      return a.distance - b.distance
    })
    .map((closestPoint) => {
      const traceId = String((closestPoint.trace as TraceInterface).uuid || closestPoint.trace.id)

      const matchingBatch = batchesStates.find(
        (batch) =>
          batch.batchId === closestPoint.trace.id ||
          batch.batchId === (closestPoint.trace as TraceInterface).uuid,
      )

      const isModelSeries = ['upper_limit', 'lower_limit', 'median', 'mean'].some((term) =>
        traceId.toLowerCase().includes(term),
      )

      if (!(isModelSeries || (matchingBatch && matchingBatch.selected))) {
        return null
      }

      const currentColorElement = circles
        .filter(function () {
          return String(select(this).attr('data-name')) === traceId
        })
        .node()

      const currentColor = currentColorElement
        ? select(currentColorElement).attr('fill') || 'red'
        : 'red'

      return {
        trace: closestPoint.trace,
        value: closestPoint.data.y,
        color: currentColor,
        closestPoint: closestPoint.data,
      }
    })
    .filter(Boolean) as SliceTooltipTracesInterface[]
}

export const getYDomainBasedOnTraces = (
  yScale: ScaleLinear<number, number> | ScaleBand<string> | null,
  allCircles: SVGCircleElement[],
  xDomain: [number, number],
  dataType?: string,
): [number, number] | null => {
  if (!yScale) {
    return null
  }
  const valuesInRange = allCircles
    .flatMap((circle) => {
      const newCx = parseFloat(circle.getAttribute('cx') || '0')
      if (newCx >= xDomain[0] && newCx <= xDomain[1]) {
        let newCYNumber: number = 0
        const newCy = parseFloat(circle.getAttribute('cy') || '0')
        // If using a band scale, map string values to numeric values
        if (dataType && dataType === 'string') {
          newCYNumber = (yScale as ScaleBand<string>)(newCy.toString()) as number
        } else if (typeof yScale === 'function') {
          newCYNumber = (yScale as ScaleLinear<number, number>).invert(newCy)
        }
        return newCYNumber
      }
      return null
    })
    .filter((value) => value !== null) as number[]

  const minVal = min(valuesInRange) || 0
  const maxVal = max(valuesInRange) || 0

  return [minVal, maxVal]
}

export const getXValues = (
  traceData: (TraceInterface | OverlayedSignalResponseInterface)[],
): string[] => (traceData ? traceData.map((item) => item.data.map((i) => i.x)).flat() : [])

export const getYValues = (
  traceData: (TraceInterface | OverlayedSignalResponseInterface)[],
): (string | number)[] =>
  traceData ? traceData.map((item) => item.data.map((i) => i.y)).flat() : []

export const getXDomain = (
  traceData: (TraceInterface | OverlayedSignalResponseInterface)[],
): [Date | number, Date | number] | null => {
  if (!traceData || (traceData && traceData.length === 0)) {
    return null
  }

  let minX: number | null = null
  let maxX: number | null = null

  traceData.forEach((trace) => {
    if (trace.data && trace.data.length > 0) {
      trace.data.forEach((point) => {
        const pointX = timeParser(point.x)
          ? (timeParser(point.x) as Date).getTime()
          : Number(point.x)

        if (minX === null || pointX < minX) {
          minX = pointX
        }

        if (maxX === null || pointX > maxX) {
          maxX = pointX
        }
      })
    }
  })

  // If we couldn't compute the domain, fall back to the original method
  if (minX === null || maxX === null) {
    // Original implementation as fallback
    const xValues = getXValues(traceData)
    if (xValues.length === 0) return null
    if (timeParser(xValues[0])) {
      return extent(xValues.map((item) => timeParser(item) as Date)) as [Date, Date]
    } else {
      const numberValues = xValues.map((item) => parseInt(item))
      return extent(numberValues) as [number, number]
    }
  }

  const range = maxX - minX
  const leftPadding = range * 0.02
  const rightPadding = range * 0.1
  minX = minX - leftPadding
  maxX = maxX + rightPadding

  // Convert back to appropriate types for the domain
  const firstTraceWithData = traceData.find((t) => t.data && t.data.length > 0)
  if (!firstTraceWithData) return null
  const firstX = firstTraceWithData.data[0].x
  if (timeParser(firstX)) {
    // For time-based data
    return [new Date(minX), new Date(maxX)] as [Date, Date]
  } else {
    // For numeric data
    return [minX, maxX] as [number, number]
  }
}

export const getYDomain = (
  traceData: (TraceInterface | OverlayedSignalResponseInterface)[],
): [number, number] | null => {
  if (!traceData || (traceData && traceData.length === 0)) {
    return null
  }
  let newCYNumber: [number, number]
  const yValues = getYValues(traceData)
  if (typeof yValues[0] === 'string') {
    newCYNumber = extent(yValues.map((item) => parseInt(item as string))) as [number, number]
  } else {
    newCYNumber = extent(yValues.map((item) => item as number)) as [number, number]
  }
  return newCYNumber
}

export const getXScale = (
  traceData: (TraceInterface | OverlayedSignalResponseInterface)[],
  range: [number, number],
  domain: [number | Date, number | Date] | null,
): ScaleTime<number, number> | ScaleLinear<number, number> | null => {
  if (!domain || !traceData || traceData.length === 0) {
    return null
  }
  const values = getXValues(traceData)
  let newXScale: ScaleTime<number, number> | ScaleLinear<number, number>
  if (values.length > 0 && timeParser(values[0])) {
    newXScale = scaleTime().domain(domain).range(range)
  } else {
    newXScale = scaleLinear().domain(domain).range(range)
  }
  return newXScale
}

export const getYScale = (
  traceData: (TraceInterface | OverlayedSignalResponseInterface)[],
  range: [number, number],
  domain: [number, number] | null,
): ScaleLinear<number, number> | ScaleBand<string> | null => {
  if (!domain || !traceData || traceData.length === 0) {
    return null
  }
  const values = getYValues(traceData)
  const isNumericData = typeof values[0] === 'number'
  let newYScale: ScaleLinear<number, number> | ScaleBand<string>
  if (isNumericData) {
    newYScale = scaleLinear().domain(domain).range(range)
  } else {
    const uniqueStrings = Array.from(new Set(values as string[]))
    newYScale = scaleBand().domain(uniqueStrings).range(range)
  }
  return newYScale
}

// ISO 8601 UTC instants as emitted inside data_table rollup values
// ("2026-08-03T04:00:00Z: 0, ..."). The backend sends timestamps as data;
// display formatting happens here, exactly like every other date in the app.
const ROLLUP_ISO_TIMESTAMP_PATTERN = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g

/**
 * Localizes the ISO timestamps inside a data_table rollup value with the same
 * getFormattedDate pipeline used for all other dates (browser timezone,
 * user's locale-dependent format).
 */
export const formatRollupValueTimestamps = (value: string, user: UserInterface | null): string =>
  value.replace(ROLLUP_ISO_TIMESTAMP_PATTERN, (isoTimestamp) =>
    getFormattedDate(isoTimestamp, user, {
      showDate: true,
      showYear: true,
      showTime: true,
      showSeconds: true,
    }),
  )

export const formatRollupValue = (
  value: string | number,
  user: UserInterface | null = null,
): string | number => {
  try {
    if (typeof value === 'string' && ROLLUP_ISO_TIMESTAMP_PATTERN.test(value)) {
      // Reset the stateful global regex before reuse in replace().
      ROLLUP_ISO_TIMESTAMP_PATTERN.lastIndex = 0
      return formatRollupValueTimestamps(value, user)
    }

    const numValue = Number(value)

    // Only format if it's a valid number and a float
    if (!Number.isNaN(numValue) && !Number.isInteger(numValue)) {
      return Math.round(numValue * 100) / 100
    }

    return value
  } catch {
    return value
  }
}

export const mapWarningToColor = (isWarning: boolean | null): string => {
  if (isWarning === null) {
    // Turning grey as this means that no data is found in backend for the signal on which the warning is based
    return 'text.disabled'
  }
  return isWarning ? 'warning.main' : 'success.main'
}

export const findProgramClassification = (
  classification: string | ProgramClassificationInterface | null | undefined,
  classifications?: ProgramClassificationInterface[],
): ProgramClassificationInterface | undefined => {
  const classificationName = getClassificationName(classification)

  if (!classificationName || !classifications?.length) {
    return undefined
  }

  const lower = classificationName.toLowerCase()
  return classifications.find(
    (item) =>
      item.id === classificationName ||
      item.name.toLowerCase() === lower ||
      item.display_name?.toLowerCase() === lower,
  )
}

export const getColorFromProgramClassification = (
  classification: string | ProgramClassificationInterface | null | undefined,
  theme: Theme,
  classifications?: ProgramClassificationInterface[],
): string => {
  const classificationName = getClassificationName(classification)
  if (!classificationName) return theme.palette.text.disabled

  // Dynamic lookup first (case-insensitive name/display name match, exact id match)
  const found = findProgramClassification(classification, classifications)
  if (found?.color) return found.color

  // Fallback to hardcoded (backward compatibility during migration)
  switch (classificationName.toLowerCase()) {
    case 'recipe':
      return theme.palette.success.main
    case 'cip':
      return purple[500]
    case 'sip':
      return deepPurple[500]
    case 'maintenance':
      return blue[500]
    case 'setup':
      return cyan[500]
    case 'downtime':
      return grey[500]
    case 'unplanned_downtime':
      return theme.palette.error.main
    case 'unassigned':
      return theme.palette.text.disabled
    default:
      return theme.palette.text.disabled
  }
}

export const classificationTranslationMapping = (
  classification: string | ProgramClassificationInterface | null | undefined,
  t: TFunction,
  classifications?: ProgramClassificationInterface[],
): string => {
  const classificationName = getClassificationName(classification)
  if (!classificationName) return ''

  // Dynamic lookup first - use display_name from API when available
  const found = findProgramClassification(classification, classifications)
  if (found?.display_name) return found.display_name

  // Fallback to hardcoded translations (backward compatibility)
  switch (classificationName.toLowerCase()) {
    case 'recipe':
      return t('programs.classification_choices.recipe')
    case 'cip':
      return t('programs.classification_choices.cip')
    case 'sip':
      return t('programs.classification_choices.sip')
    case 'maintenance':
      return t('programs.classification_choices.maintenance')
    case 'setup':
      return t('programs.classification_choices.setup')
    case 'downtime':
      return t('programs.classification_choices.downtime')
    case 'unplanned_downtime':
      return t('programs.classification_choices.unplanned_downtime')
    case 'unassigned':
      return t('programs.classification_choices.unassigned')
    default:
      return classificationName
  }
}

export const transformStartDate = (dateString: string, summarizeBy: string): string => {
  const date = new Date(dateString)

  if (summarizeBy === 'day') {
    date.setHours(0, 0, 0, 0) // Set to closest past midnight
  } else if (summarizeBy === 'week' || summarizeBy === 'calendar_week') {
    const day = date.getDay() // 0 is Sunday, 1 is Monday, ..., 6 is Saturday
    const diff = day === 0 ? 6 : day - 1 // If it's Sunday (0), go back 6 days; otherwise, go back to Monday
    date.setDate(date.getDate() - diff)
    date.setHours(0, 0, 0, 0) // Set time to midnight
  }

  return date.toISOString()
}

export const filterWarnings = (warnings: CockpitWarningInterface[]): CockpitWarningInterface[] =>
  warnings?.filter((warning) => warning.eval_result.is_warning === true)

export const getProductionTimelineYPosition = (
  originalY: number,
  density: DensityInterface,
  rectHeight: number,
  bandWidth: number,
  entry?: boolean,
): number => {
  const newHeight = density.id !== 'see_all' ? density.height : rectHeight

  // Calculate centered yPosition within the band
  const centeredYPosition = originalY + (bandWidth - newHeight) / 2

  // Adjust for entries (if necessary)
  return entry ? centeredYPosition + newHeight : centeredYPosition
}

// Validate that time is only mm:ss
export const validateTimeMinuteSeconds = (time: string): boolean => {
  const timeArray = time.split(':')
  if (timeArray.length !== 2) {
    return false
  }
  if (timeArray[0].length !== 2 || timeArray[1].length !== 2) {
    return false
  }
  if (Number(timeArray[1]) > 59) {
    return false
  }
  return true
}

/** The rules that compare against a value set rather than a single value (AB#6081). */
export const SET_RULES = ['in', 'not in'] as const

export const isSetRule = (rule: string): boolean => (SET_RULES as readonly string[]).includes(rule)

export const getProgramTriggerLabel = (
  name: string,
  rule: string,
  active_threshold: number | string | null,
  /**
   * The trigger's `threshold_display`, when it has one. A signal-based trigger
   * (AB#5923) has no static threshold, so `active_threshold` is null there — and
   * `Number(null)` is 0, which would render the condition as "... > 0" rather than
   * naming the signal it actually compares against.
   */
  thresholdDisplay?: string,
): string => {
  // Only when there is no static number: a signal-based trigger has
  // `active_threshold === null`. Taking the shortcut whenever a display string is
  // present would bypass the numeric normalization below and change every existing
  // static label (5 → "5.00", 78.456 → the raw API string instead of 78.46).
  // A set rule (AB#6081) also has no static threshold, but its display string is a
  // list rather than a single value. Braces make a list readable where a bare
  // "step in 12, 15" reads as two separate things, and each member goes through the
  // same numeric normalization as a scalar threshold so a step chain reads
  // "{12, 15}" rather than "{12.0, 15.0}".
  if (isSetRule(rule) && thresholdDisplay) {
    const members = thresholdDisplay
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => {
        const numeric = Number(entry)
        if (Number.isNaN(numeric)) return entry
        return numeric % 1 === 0 ? numeric.toString() : numeric.toFixed(2)
      })
    return `${name} ${rule} {${members.join(', ')}}`
  }

  if (thresholdDisplay && (active_threshold === null || active_threshold === undefined)) {
    return `${name} ${rule} ${thresholdDisplay}`
  }

  // Ensure active_threshold is a number
  const numeric_threshold = Number(active_threshold)

  // Check if it's a valid number
  if (Number.isNaN(numeric_threshold)) {
    return `${name} ${rule} ${active_threshold}` // Return as-is if it's not a number (e.g., a string)
  }

  // Check if active_threshold is a whole number
  const active_threshold_string =
    numeric_threshold % 1 === 0 ? numeric_threshold.toString() : numeric_threshold.toFixed(2)
  return `${name} ${rule} ${active_threshold_string}`
}

export const getTitleForAnomaly = (
  anomalyDetectionType: AnomalyWithReasonInterface['anomaly_detection_type'],
  step: AnomalyWithReasonInterface['step'],
  signalName: string,
  stepString: string,
  description?: AnomalyWithReasonInterface['step_description']['description'],
): string => {
  const safeStep = step ?? ''
  const safeDescription = description ? ` (${description})` : ''
  const safeSignalName = signalName ?? ''
  if (anomalyDetectionType === 'step_signal' || !anomalyDetectionType) {
    return `${stepString} ${safeStep}${safeDescription}`.trim()
  }

  return `${safeSignalName}`.trim()
}

export const tStartTEndMinutesAndSeconds = (tStart: Date, tEnd: Date): string => {
  const durationInSeconds = Math.floor((tEnd.getTime() - tStart.getTime()) / 1000)
  const minutes = Math.floor(durationInSeconds / 60)
    .toString()
    .padStart(2, '0')
  const seconds = (durationInSeconds % 60).toString().padStart(2, '0')

  return `${minutes}:${seconds}`
}

export function normalizeTraces<T extends TraceInterface | OverlayedSignalResponseInterface>(
  traceData: T[],
): T[] {
  return traceData.map((trace) => {
    const yValues = trace.data
      .map((d) => (typeof d.y === 'number' ? d.y : null))
      .filter((y): y is number => y !== null)

    const yMin = yValues.length > 0 ? Math.min(...yValues) : 0
    const yMax = yValues.length > 0 ? Math.max(...yValues) : 1
    const isFlat = yMin === yMax
    const yRange = isFlat ? 1 : yMax - yMin // avoid divide by zero

    return {
      ...trace,
      data: trace.data.map((point) => ({
        ...point,
        original_y: point.y,
        y:
          typeof point.y === 'number'
            ? isFlat
              ? 0.5 // center the flat line
              : (point.y - yMin) / yRange
            : point.y,
      })),
    }
  })
}

export const isoDurationToMinutes = (durationStr: string): number => {
  const regex = /^(-?)P(?:([0-9]+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/
  const match = durationStr.match(regex)
  if (!match) {
    return 0
  }

  const [, negative, days, hours, minutes, seconds] = match
  const sign = negative === '-' ? -1 : 1

  const totalMinutes =
    (+days || 0) * 1440 + // 1440 = 24 * 60
    (+hours || 0) * 60 +
    (+minutes || 0) +
    (+seconds || 0) / 60

  return sign * totalMinutes
}

export const getModelLineColor = (name: string, theme: Theme): string => {
  switch (name) {
    case 'Upper Limit':
      return theme.palette.error.main
    case 'Lower Limit':
      return theme.palette.warning.main
    case 'Median':
      return theme.palette.success.main
    case 'Mean':
      return theme.palette.info.main
    default:
      return theme.palette.grey[500]
  }
}

// / this function calculates the time in a human-readable format
// / it takes a number of hours and returns a string in the format "Xd Xh Xm Xs"
// / where X is the number of days, hours, minutes, and seconds respectively
// / if there are no days, hours, or minutes, it will only show seconds
// / if there are no seconds, it will show "0s"

export const calculateTime = (hours: number): string => {
  const totalSeconds = Math.round(hours * 3600)

  const days = Math.floor(totalSeconds / 86400)
  const hoursPart = Math.floor((totalSeconds % 86400) / 3600)
  const minutesPart = Math.floor((totalSeconds % 3600) / 60)
  const secondsPart = totalSeconds % 60

  let str = ''
  if (days) {
    str += `${days}d `
  }
  if (hoursPart) {
    str += `${hoursPart}h `
  }
  if (minutesPart) {
    str += `${minutesPart}m `
  }
  if (secondsPart || !str) {
    str += `${secondsPart}s`
  }

  return str.trim()
}

export const getIsMobile = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false
  }

  const ua = navigator.userAgent.toLowerCase()

  const isMobileUA = /android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)

  return isMobileUA
}
